/**
 * [Session C-A CP-9] Manual Update Panel - seven-category checkbox UI.
 *
 * User checks one or more boxes, clicks "Run Update", and the panel
 * fires the mapped admin job triggers in dependency order:
 *   1 Player & Depth Charts    → sleeper_player_identity + depth_chart_change_check
 *   2 Injuries                  → sleeper_player_identity (same job - dedup below)
 *   3 Schedule & Vegas Lines    → schedule_ingest + schedule_change_check
 *                                 + odds_api_game_lines_refresh
 *                                 + position_matchup_compute
 *   4 Team Metrics              → nflfastr_team_metrics_compute
 *   5 Player Props              → odds_api_player_props_refresh
 *   6 News                      → GREYED OUT (Coming with C-B)
 *   7 Full Recomputation        → full_axis_recomputation (or existing name)
 *
 * Each checkbox maps to POST /api/admin/jobs/{name}/trigger which already exists.
 * The panel dedupes shared jobs across categories and always runs Full
 * Recomputation LAST if selected.
 */
import { useState, useMemo } from "react";
import axios from "axios";
import { Play, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category → ordered list of job names to trigger. Dependency ordering
// within a category matters (schedule before matchup compute etc.).
const CATEGORY_JOBS = {
  player_depth: ["sleeper_player_identity", "depth_chart_change_check"],
  injuries: ["sleeper_player_identity"],
  schedule_vegas: [
    "schedule_ingest",
    "schedule_change_check",
    "odds_api_game_lines_refresh",
    "position_matchup_compute",
  ],
  team_metrics: ["nflfastr_team_metrics_compute"],
  player_props: ["odds_api_player_props_refresh"],
  full_recompute: ["full_axis_recomputation"],
};

const CATEGORY_META = [
  { key: "player_depth", label: "Player & Depth Charts", desc: "Roster moves, depth chart changes" },
  { key: "injuries", label: "Injuries", desc: "Injury status, practice participation" },
  { key: "schedule_vegas", label: "Schedule & Vegas Lines", desc: "Schedule flex, game lines, matchup cascade" },
  { key: "team_metrics", label: "Team Metrics", desc: "Pace, ratios, efficiency, PA, motion" },
  { key: "player_props", label: "Player Props", desc: "Projected PPG from Odds API" },
  { key: "news", label: "News", desc: "Beat writer intelligence - Coming with C-B", disabled: true },
  { key: "full_recompute", label: "Full Recomputation", desc: "All axes → PGF Rank → PGF Value" },
];

const CATEGORY_ORDER = [
  "player_depth", "injuries", "schedule_vegas",
  "team_metrics", "player_props", "full_recompute",
];

export default function ManualUpdatePanel() {
  const [selected, setSelected] = useState({});
  const [running, setRunning] = useState(false);
  const [jobStatus, setJobStatus] = useState({});
  const [summary, setSummary] = useState(null);

  const jobsToRun = useMemo(() => {
    const jobs = [];
    for (const cat of CATEGORY_ORDER) {
      if (!selected[cat] || cat === "news") continue;
      for (const job of CATEGORY_JOBS[cat]) {
        if (!jobs.includes(job)) jobs.push(job);
      }
    }
    return jobs;
  }, [selected]);

  const toggle = (key) => {
    if (CATEGORY_META.find((c) => c.key === key)?.disabled) return;
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const runUpdate = async () => {
    if (jobsToRun.length === 0) return;
    setRunning(true);
    setJobStatus({});
    setSummary(null);
    const headers = authHeaders();
    let succeeded = 0;
    let failed = 0;
    for (const job of jobsToRun) {
      setJobStatus((prev) => ({ ...prev, [job]: "running" }));
      try {
        await axios.post(`${API}/admin/jobs/${job}/trigger`, {}, { headers, timeout: 20000 });
        setJobStatus((prev) => ({ ...prev, [job]: "queued" }));
        succeeded += 1;
      } catch (e) {
        setJobStatus((prev) => ({ ...prev, [job]: "failed" }));
        failed += 1;
      }
    }
    setSummary({ succeeded, failed, total: jobsToRun.length });
    setRunning(false);
  };

  return (
    <section
      data-testid="admin-section-manual-update-panel"
      className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5"
    >
      <h2 className="font-display font-bold text-base uppercase mb-3 flex items-center gap-2">
        <Play size={14} className="text-[#FF6B00]" /> Manual Update Panel
      </h2>
      <p className="font-body text-sm text-[#9E9E9E] mb-4">
        Select data categories to refresh, then click Run Update. Jobs fire in
        dependency order (Full Recomputation always runs last).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {CATEGORY_META.map((cat) => {
          const isChecked = !!selected[cat.key];
          const isDisabled = cat.disabled;
          return (
            <label
              key={cat.key}
              data-testid={`manual-update-category-${cat.key}`}
              className={`flex items-start gap-3 p-3 rounded-lg border transition ${
                isDisabled
                  ? "border-[#252525] bg-[#141414] opacity-50 cursor-not-allowed"
                  : isChecked
                  ? "border-[#FF6B00] bg-[#1F1811] cursor-pointer"
                  : "border-[#252525] bg-[#141414] hover:border-[#3A3A3A] cursor-pointer"
              }`}
              title={isDisabled ? "Coming with In-Season Analytics (Session C-B)" : undefined}
            >
              <input
                type="checkbox"
                data-testid={`manual-update-checkbox-${cat.key}`}
                checked={isChecked}
                disabled={isDisabled || running}
                onChange={() => toggle(cat.key)}
                className="mt-1 h-4 w-4 accent-[#FF6B00]"
              />
              <div>
                <div className="font-body font-bold text-sm text-[#F5F5F5]">
                  {cat.label}
                </div>
                <div className="font-body text-xs text-[#9E9E9E]">{cat.desc}</div>
              </div>
            </label>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <button
          type="button"
          data-testid="manual-update-run-button"
          disabled={running || jobsToRun.length === 0}
          onClick={runUpdate}
          className={`font-body font-bold text-sm px-4 py-2 rounded-lg transition ${
            running || jobsToRun.length === 0
              ? "bg-[#252525] text-[#5E5E5E] cursor-not-allowed"
              : "bg-[#FF6B00] text-black hover:bg-[#FF8534]"
          }`}
        >
          {running ? "Running…" : `Run Update (${jobsToRun.length} job${jobsToRun.length === 1 ? "" : "s"})`}
        </button>
      </div>

      {(running || Object.keys(jobStatus).length > 0) && (
        <ul data-testid="manual-update-progress" className="space-y-1 mb-3">
          {jobsToRun.map((job) => {
            const status = jobStatus[job];
            return (
              <li
                key={job}
                data-testid={`manual-update-job-status-${job}`}
                className="flex items-center gap-2 font-body text-sm text-[#F5F5F5]"
              >
                {status === "running" && <Loader2 size={14} className="animate-spin text-[#FFB020]" />}
                {status === "queued" && <CheckCircle2 size={14} className="text-[#00C853]" />}
                {status === "failed" && <XCircle size={14} className="text-[#FF4444]" />}
                {!status && <span className="w-[14px] h-[14px] rounded-full border border-[#3A3A3A]" />}
                <span className="font-mono">{job}</span>
                <span className="text-xs text-[#9E9E9E]">- {status || "queued"}</span>
              </li>
            );
          })}
        </ul>
      )}

      {summary && (
        <div
          data-testid="manual-update-summary"
          className="font-body text-sm text-[#F5F5F5] p-3 rounded-lg bg-[#141414] border border-[#252525]"
        >
          {summary.succeeded}/{summary.total} jobs queued. {summary.failed} failed.
        </div>
      )}
    </section>
  );
}
